// FILE: src/components/case-studies/learner/CaseReviewScreen.js
// Post-completion review: score summary, ECO-domain and question-type
// breakdowns, and per-question review with the information that was
// available at that point (stage-scoped narrative above each stage group).

import React, { useEffect, useMemo, useState } from 'react';
import { getCaseReview } from '../../../services/caseStudyService';
import { getQuestionType } from '../../../questionTypes';
import { buildBreakdowns } from './caseRunnerUtils';
import { ArtifactView } from './CasePanel';

const T = {
  line: 'var(--line, #e5e8f1)', muted: 'var(--muted, #5a6582)',
  brand: 'var(--brand, #2c63ff)', brandSoft: 'var(--brand-soft, #eaf0ff)',
  good: 'var(--good, #16a571)', goodSoft: 'var(--good-soft, #e6f7ef)',
  bad: 'var(--bad, #d64545)', badSoft: 'var(--bad-soft, #fde9ea)', panel: 'var(--panel, #ffffff)',
};
const card = { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: 16, marginBottom: 14 };

export default function CaseReviewScreen({ attemptId, caseMeta, onBack }) {
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);
  const [openArtifact, setOpenArtifact] = useState(null);

  useEffect(() => {
    getCaseReview(attemptId).then(setReview).catch((e) => setError(e.message));
  }, [attemptId]);

  const breakdowns = useMemo(
    () => (review ? buildBreakdowns(review.stages, review.responses) : null), [review]);

  if (error) return <div style={{ ...card, color: T.bad }} data-testid="review-error">{error}</div>;
  if (!review) return <div style={{ ...card, color: T.muted }}>Loading review…</div>;

  const att = review.attempt;
  const respBySeq = new Map(review.responses.map((r) => [r.case_sequence, r]));
  const mins = Math.round((att.elapsed_seconds || 0) / 60);

  return (
    <div data-testid="case-review">
      <div style={{ ...card, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '0.72rem', color: T.muted, textTransform: 'uppercase', fontWeight: 800 }}>Score</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: att.score >= 75 ? T.good : T.bad }} data-testid="review-score">
            {att.score}%
          </div>
          <div style={{ fontSize: '0.78rem', color: T.muted }}>{att.correct_count} of {att.question_count} correct · ~{mins} min</div>
        </div>
        {breakdowns && [['By ECO domain', breakdowns.byDomain], ['By question type', breakdowns.byType]].map(([label, rows]) => (
          <div key={label} style={{ minWidth: 200 }}>
            <div style={{ fontSize: '0.72rem', color: T.muted, textTransform: 'uppercase', fontWeight: 800, marginBottom: 4 }}>{label}</div>
            {rows.map((r) => (
              <div key={r.key} style={{ display: 'flex', gap: 8, fontSize: '0.8rem', padding: '2px 0' }}>
                <span style={{ flex: 1 }}>{r.key}</span>
                <span style={{ fontWeight: 700 }}>{r.correct}/{r.total}</span>
              </div>
            ))}
          </div>
        ))}
        <span style={{ flex: 1 }} />
        <button type="button" data-testid="review-back" onClick={onBack}
          style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.line}`, background: 'transparent', cursor: 'pointer', fontWeight: 600 }}>
          ← Back to case list
        </button>
      </div>

      {review.stages.map((st) => (
        <div key={st.sequence}>
          <div style={{ margin: '14px 0 8px', fontSize: '0.8rem', fontWeight: 800, color: T.brand }}>
            Stage {st.sequence}: {st.title}
          </div>
          {(st.artifacts || []).length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {st.artifacts.map((a) => (
                <button key={a.id} type="button" style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${T.line}`, background: T.panel, cursor: 'pointer', fontSize: '0.76rem' }}
                  onClick={() => setOpenArtifact(openArtifact && openArtifact.id === a.id ? null : a)}>
                  📎 {a.title}
                </button>
              ))}
            </div>
          )}
          {openArtifact && (st.artifacts || []).some((a) => a.id === openArtifact.id) && (
            <ArtifactView artifact={openArtifact} onClose={() => setOpenArtifact(null)} />
          )}
          {(st.questions || []).map((q) => {
            const r = respBySeq.get(q.case_sequence);
            if (!r) return null;
            const ReviewR = getQuestionType(q.type)?.ReviewRenderer;
            return (
              <div key={q.id} style={card} data-testid={`review-q-${q.case_sequence}`}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'baseline' }}>
                  <strong>Q{q.case_sequence}</strong>
                  <span style={{ fontSize: '0.7rem', color: T.muted, textTransform: 'uppercase' }}>{q.type.replace('_', ' ')}</span>
                  <span style={{ padding: '1px 9px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700,
                    background: r.is_correct ? T.goodSoft : T.badSoft, color: r.is_correct ? T.good : T.bad }}>
                    {r.is_correct ? 'Correct' : 'Incorrect'}
                  </span>
                  {r.was_flagged && <span style={{ fontSize: '0.72rem' }}>🚩</span>}
                  <span style={{ flex: 1 }} />
                  {r.time_spent_seconds != null && <span style={{ fontSize: '0.72rem', color: T.muted }}>{r.time_spent_seconds}s</span>}
                </div>
                <div style={{ fontSize: '0.9rem', marginBottom: 10 }}>{q.question}</div>
                {ReviewR && <ReviewR question={q} selected={r.selected_answer || []} correct={r.correct_answers || []} />}
                {r.explanation && (
                  <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: T.brandSoft, fontSize: '0.84rem', whiteSpace: 'pre-wrap' }}>
                    <strong>Why:</strong> {r.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
